import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface RelativityConcept {
  name: string;
  formula: string;
  explain: string;
  color: string;
}

interface SpacetimeFact {
  title: string;
  icon: string;
  detail: string;
  color: string;
}

interface RelativityMilestone {
  year: string;
  event: string;
  color: string;
}

interface RelativityTemplateProps {
  topic: string;
  subtitle: string;
  concepts: RelativityConcept[];
  facts: SpacetimeFact[];
  milestones: RelativityMilestone[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: RelativityTemplateProps = {
  topic: '相对论与时空物理',
  subtitle: 'Relativity & Spacetime Physics',
  concepts: [
    {
      name: '质能等价',
      formula: 'E = mc²',
      explain: '质量与能量等价，微小质量可转化为巨大能量',
      color: '#f59e0b',
    },
    {
      name: '时间膨胀',
      formula: 'Δt\' = γΔt',
      explain: '运动越快，时间流逝越慢；接近光速时效果显著',
      color: '#3b82f6',
    },
    {
      name: '长度收缩',
      formula: 'L\' = L/γ',
      explain: '运动方向上的长度会缩短，垂直方向不变',
      color: '#8b5cf6',
    },
    {
      name: '引力弯曲时空',
      formula: 'Gμν = 8πG/c⁴ · Tμν',
      explain: '质量告诉时空如何弯曲，时空告诉物质如何运动',
      color: '#ec4899',
    },
  ],
  facts: [
    { title: '光速不变', icon: '💡', detail: '真空中光速 c ≈ 3×10⁸ m/s，是宇宙速度上限', color: '#f59e0b' },
    { title: '引力波', icon: '🌊', detail: '时空涟漪，由加速质量产生，2015年首次探测', color: '#3b82f6' },
    { title: '黑洞', icon: '🕳️', detail: '时空极度弯曲，连光都无法逃逸的天体', color: '#8b5cf6' },
    { title: 'GPS修正', icon: '🛰️', detail: '卫星因时间膨胀每天快38微秒，需相对论修正', color: '#22c55e' },
  ],
  milestones: [
    { year: '1905', event: '爱因斯坦发表狭义相对论', color: '#f59e0b' },
    { year: '1915', event: '广义相对论完成', color: '#3b82f6' },
    { year: '1919', event: '日食验证光线弯曲', color: '#22c55e' },
    { year: '2015', event: 'LIGO探测到引力波', color: '#8b5cf6' },
    { year: '2019', event: '首张黑洞照片公布', color: '#ec4899' },
  ],
  stats: [
    { value: '299,792 km/s', label: '光速 c' },
    { value: 'E=mc²', label: '最著名的物理公式' },
    { value: '100+年', label: '已被无数实验验证' },
  ],
  bottomQuote: '想象力比知识更重要。知识是有限的，想象力概括世界上的一切 —— 爱因斯坦',
};

export const RelativityTemplate: React.FC<
  Partial<RelativityTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const conceptStart = 60;
  const gridStart = 30;
  const factStart = 220;
  const milestoneStart = 340;
  const statsStart = 440;
  const quoteStart = 490;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const gridWarp = interpolate(frame, [gridStart, gridStart + 100], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const lightConeDraw = interpolate(frame, [gridStart + 50, gridStart + 120], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(150deg, #020210 0%, #0a0820 50%, #060618 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Star field */}
      {Array.from({ length: 70 }).map((_, i) => {
        const x = (i * 251) % 1920;
        const y = (i * 167) % 1080;
        const size = 1 + (i % 3) * 0.5;
        const twinkle = 0.3 + 0.7 * Math.abs(Math.sin(frame * 0.03 + i * 1.7));
        return (
          <div
            key={`star-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#fff',
              opacity: 0.15 * twinkle,
            }}
          />
        );
      })}

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 18}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#1a1040',
            borderRadius: 8,
            fontSize: 20,
            fontWeight: 700,
            color: '#f59e0b',
            flexShrink: 0,
            border: '1px solid #f59e0b30',
          }}
        >
          🔬 硬核科普
        </div>
        <h1 style={{ fontSize: 48, fontWeight: 900, margin: 0, color: '#f8fafc' }}>
          {p.topic}
        </h1>
        <p style={{ fontSize: 24, color: '#64748b', margin: 0, whiteSpace: 'nowrap' }}>
          {p.subtitle}
        </p>
      </div>

      {/* Main body: grid + stacked rows — avoids absolute overlap between concepts and facts */}
      <div
        style={{
          position: 'absolute',
          top: 100,
          left: 40,
          right: 40,
          bottom: 200,
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 360px) 1fr',
            gap: 20,
            alignItems: 'start',
          }}
        >
          {/* ===== LEFT: Core Concepts ===== */}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 26,
                color: '#64748b',
                marginBottom: 10,
                letterSpacing: '0.08em',
                fontWeight: 600,
              }}
            >
              核心概念
            </div>
            {p.concepts.map((c, i) => {
              const s = spring({
                frame: frame - (conceptStart + i * 22),
                fps,
                config: { damping: 14, stiffness: 110 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0a0a1e',
                    borderRadius: 12,
                    padding: '11px 14px',
                    marginBottom: 10,
                    borderLeft: `4px solid ${c.color}`,
                    opacity: s,
                    transform: `translateX(${(1 - s) * -40}px)`,
                  }}
                >
                  <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.name}</div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 900,
                      color: '#f8fafc',
                      fontFamily: '"SF Mono", "Fira Code", monospace',
                      margin: '3px 0',
                      lineHeight: 1.25,
                      wordBreak: 'break-word',
                    }}
                  >
                    {c.formula}
                  </div>
                  <div style={{ fontSize: 18, color: '#cbd5e1', lineHeight: 1.55 }}>{c.explain}</div>
                </div>
              );
            })}
          </div>

          {/* ===== RIGHT: Spacetime + Light cone (left-aligned) + 相对论效应 (column) ===== */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 20,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 14,
                flexShrink: 0,
              }}
            >
              {/* Spacetime Grid Warping SVG */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
            <svg width="700" height="475" viewBox="0 0 560 380" style={{ flexShrink: 0 }}>
        <text x="40" y="22" fill="#64748b" fontSize="22" textAnchor="start" fontWeight="700">
          时空弯曲 · Spacetime Curvature
        </text>

        {/* Grid lines (warped by mass) */}
        {Array.from({ length: 13 }).map((_, i) => {
          const baseY = 50 + i * 24;
          const points: string[] = [];
          for (let x = 40; x <= 520; x += 8) {
            const dx = x - 280;
            const dy = baseY - 200;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const warpAmount = dist < 120 ? (120 - dist) * 0.4 * gridWarp : 0;
            const wy = baseY + warpAmount;
            points.push(`${x},${wy.toFixed(1)}`);
          }
          return (
            <polyline
              key={`h-${i}`}
              points={points.join(' ')}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="1.2"
              opacity={0.15 + gridWarp * 0.2}
            />
          );
        })}
        {Array.from({ length: 25 }).map((_, i) => {
          const baseX = 40 + i * 20;
          const points: string[] = [];
          for (let y = 50; y <= 340; y += 8) {
            const dx = baseX - 280;
            const dy = y - 200;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const warpAmountX = dist < 120 ? dx * (120 - dist) * 0.003 * gridWarp : 0;
            const warpAmountY = dist < 120 ? (120 - dist) * 0.4 * gridWarp : 0;
            points.push(`${(baseX + warpAmountX).toFixed(1)},${(y + warpAmountY).toFixed(1)}`);
          }
          return (
            <polyline
              key={`v-${i}`}
              points={points.join(' ')}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="1.2"
              opacity={0.15 + gridWarp * 0.2}
            />
          );
        })}

        {/* Central mass */}
        <circle
          cx="280"
          cy={200 + gridWarp * 35}
          r={16 + gridWarp * 8}
          fill="#f59e0b"
          opacity={0.3 + gridWarp * 0.5}
        />
        <circle
          cx="280"
          cy={200 + gridWarp * 35}
          r={26 + gridWarp * 14}
          fill="none"
          stroke="#f59e0b"
          strokeWidth="1.5"
          opacity={gridWarp * 0.4}
        />
        <text
          x="280"
          y={200 + gridWarp * 35 + 52}
          fill="#f59e0b"
          fontSize="19"
          textAnchor="middle"
          fontWeight="700"
          opacity={gridWarp}
        >
          质量 M
        </text>

        <text
          x="280"
          y="372"
          fill="#94a3b8"
          fontSize="18"
          textAnchor="middle"
          opacity={interpolate(gridWarp, [0.5, 0.8], [0, 0.7], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}
        >
          质量越大，时空弯曲越剧烈
        </text>
            </svg>
              </div>

              {/* Light Cone */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
            <svg width="400" height="329" viewBox="0 0 340 280" style={{ flexShrink: 0 }}>
        <text x="20" y="20" fill="#64748b" fontSize="20" textAnchor="start" fontWeight="700">
          光锥 · Light Cone
        </text>

        {/* Future cone */}
        <path
          d="M170,150 L80,30 L260,30 Z"
          fill="#f59e0b08"
          stroke="#f59e0b"
          strokeWidth="2"
          strokeDasharray="480"
          strokeDashoffset={480 * (1 - lightConeDraw)}
          opacity="0.6"
        />
        <text x="170" y="62" fill="#f59e0b" fontSize="20" textAnchor="middle" fontWeight="700"
          opacity={lightConeDraw}
        >
          未来光锥
        </text>

        {/* Past cone */}
        <path
          d="M170,150 L80,270 L260,270 Z"
          fill="#3b82f608"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeDasharray="480"
          strokeDashoffset={480 * (1 - lightConeDraw)}
          opacity="0.6"
        />
        <text x="170" y="252" fill="#3b82f6" fontSize="20" textAnchor="middle" fontWeight="700"
          opacity={lightConeDraw}
        >
          过去光锥
        </text>

        {/* Present event */}
        <circle cx="170" cy="150" r="6" fill="#ec4899" opacity={lightConeDraw} />
        <text x="198" y="156" fill="#ec4899" fontSize="20" fontWeight="700" opacity={lightConeDraw}>
          事件
        </text>

        {/* Spacelike region label */}
        <text x="28" y="156" fill="#64748b" fontSize="20" fontWeight="600"
          opacity={interpolate(lightConeDraw, [0.6, 1], [0, 0.7], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >类空区域</text>
        <text x="250" y="156" fill="#64748b" fontSize="20" fontWeight="600"
          opacity={interpolate(lightConeDraw, [0.6, 1], [0, 0.7], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >类空区域</text>
            </svg>
              </div>
            </div>

            {/* 相对论效应 — vertical, to the right of light cone */}
            <div style={{ flex: 1, minWidth: 220, maxWidth: 420, display: 'flex', flexDirection: 'column' }}>
              <div
                style={{
                  fontSize: 26,
                  color: '#64748b',
                  marginBottom: 10,
                  letterSpacing: '0.08em',
                  fontWeight: 600,
                }}
              >
                相对论效应
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {p.facts.map((f, i) => {
                  const s = spring({
                    frame: frame - (factStart + i * 16),
                    fps,
                    config: { damping: 14, stiffness: 120 },
                  });
                  return (
                    <div
                      key={i}
                      style={{
                        width: '100%',
                        minWidth: 0,
                        background: '#0a0a1e',
                        borderRadius: 14,
                        padding: '14px 12px',
                        borderTop: `3px solid ${f.color}`,
                        opacity: s,
                        transform: `translateY(${(1 - s) * 20}px)`,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: 12,
                        }}
                      >
                        <div style={{ fontSize: 30, lineHeight: 1, flexShrink: 0 }}>{f.icon}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 24, fontWeight: 800, color: f.color, lineHeight: 1.25 }}>
                            {f.title}
                          </div>
                          <div style={{ fontSize: 20, color: '#cbd5e1', lineHeight: 1.55 }}>{f.detail}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ===== Milestones Timeline ===== */}
        <div style={{ flexShrink: 0, marginTop: 4 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          里程碑
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, flexWrap: 'wrap' }}>
          {p.milestones.map((m, i) => {
            const s = spring({
              frame: frame - (milestoneStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const isLast = i === p.milestones.length - 1;
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    textAlign: 'center',
                    opacity: s,
                    transform: `scale(${s})`,
                    minWidth: 140,
                  }}
                >
                  <div style={{ fontSize: 28, fontWeight: 900, color: m.color }}>{m.year}</div>
                  <div style={{ fontSize: 20, color: '#cbd5e1', marginTop: 4, lineHeight: 1.45 }}>{m.event}</div>
                </div>
                {!isLast && (
                  <div
                    style={{
                      width: 50,
                      height: 2,
                      background: `linear-gradient(90deg, ${m.color}, ${p.milestones[i + 1].color})`,
                      flexShrink: 0,
                      opacity: interpolate(frame, [milestoneStart + i * 16 + 10, milestoneStart + i * 16 + 25], [0, 0.6], {
                        extrapolateLeft: 'clamp',
                        extrapolateRight: 'clamp',
                      }),
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        </div>
      </div>

      {/* ===== Stats ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 40,
          right: 40,
          display: 'flex',
          gap: 16,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0a0a1e',
                borderRadius: 12,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #1e1b4b',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 8,
                  flexWrap: 'nowrap',
                }}
              >
                <span style={{ fontSize: 30, fontWeight: 900, color: '#f59e0b' }}>{st.value}</span>
                <span style={{ fontSize: 20, color: '#94a3b8' }}>{st.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: 40,
          right: 40,
          background: '#0a0a1e',
          borderRadius: 14,
          padding: '14px 20px',
          borderTop: '3px solid #f59e0b',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🔬</span>
          <span style={{ fontSize: 20, color: '#e2e8f0', lineHeight: 1.5 }}>{p.bottomQuote}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #f59e0b, #3b82f6, #8b5cf6, #ec4899)',
        }}
      />
    </AbsoluteFill>
  );
};
